// components/cards/GeoCardImage.client.tsx
"use client";

import { useState } from "react";

export type GeoCardImageClientProps = {
  src: string;
  alt: string;
  fallbackSrc?: string;
  loading?: "lazy" | "eager";
  decoding?: "sync" | "async" | "auto";
};

// Genera srcset per immagini responsive
function generateSrcSet(basePath: string, widths: number[], extension: string): string {
  return widths
    .map((width) => {
      // Estrae il nome base (es. "/regioni/campania" da "/regioni/campania.webp")
      const base = basePath.replace(/\.(webp|avif|jpg|jpeg|png)$/i, "");
      return `${base}-${width}w.${extension} ${width}w`;
    })
    .join(", ");
}

// Genera il srcset per il fallback (versione legacy senza suffisso)
function getFallbackSrc(basePath: string, extension: string): string {
  const base = basePath.replace(/\.(webp|avif|jpg|jpeg|png)$/i, "");
  return `${base}.${extension}`;
}

export function GeoCardImageClient({
  src,
  alt,
  fallbackSrc,
  loading = "lazy",
  decoding = "async",
}: GeoCardImageClientProps) {
  const [useFallback, setUseFallback] = useState(false);

  // Dimensioni disponibili per immagini responsive
  const widths = [400, 600, 800, 960];
  
  // Estrae il percorso base (es. "/regioni/campania" da "/regioni/campania.webp")
  const basePath = src.replace(/\.(webp|avif|jpg|jpeg|png)$/i, "");
  const extension = src.match(/\.(webp|avif|jpg|jpeg|png)$/i)?.[1] || "webp";

  // Genera srcset per AVIF e WebP
  const avifSrcSet = generateSrcSet(basePath, widths, "avif");
  const webpSrcSet = generateSrcSet(basePath, widths, "webp");
  const avifSrc = getFallbackSrc(basePath, "avif");
  const webpSrc = getFallbackSrc(basePath, "webp");
  const fallbackImgSrc = useFallback ? (fallbackSrc || "/placeholder-landscape.svg") : webpSrc;

  const handleImageError = () => {
    // Prova prima con il fallback (es. immagine regione)
    if (fallbackSrc && !useFallback) {
      setUseFallback(true);
    }
    // Se anche il fallback fallisce, l'immagine mostrerà il placeholder tramite il fallback
  };

  // Se stiamo usando il fallback, usa un semplice img tag
  if (useFallback && fallbackSrc) {
    return (
      <img
        src={fallbackSrc}
        alt={alt}
        width={800}
        height={600}
        sizes="(min-width:1280px) 25vw, (min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
        loading={loading}
        decoding={decoding}
        onError={handleImageError}
        style={{
          objectFit: "cover",
          objectPosition: "center",
          width: "100%",
          height: "100%",
          position: "absolute",
          inset: 0,
        }}
      />
    );
  }

  // Usa picture element con AVIF e WebP per immagini responsive
  return (
    <picture>
      {/* AVIF con srcset responsive */}
      <source
        type="image/avif"
        srcSet={avifSrcSet}
        sizes="(min-width:1280px) 25vw, (min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
      />
      {/* WebP con srcset responsive */}
      <source
        type="image/webp"
        srcSet={webpSrcSet}
        sizes="(min-width:1280px) 25vw, (min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
      />
      {/* Fallback img con srcset WebP */}
      <img
        src={fallbackImgSrc}
        srcSet={webpSrcSet}
        alt={alt}
        width={800}
        height={600}
        sizes="(min-width:1280px) 25vw, (min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
        loading={loading}
        decoding={decoding}
        onError={handleImageError}
        style={{
          objectFit: "cover",
          objectPosition: "center",
          width: "100%",
          height: "100%",
          position: "absolute",
          inset: 0,
        }}
      />
    </picture>
  );
}












